import React from 'react';
import {
  Badge, Button, EmptyState, ErrorState, PageHeader, Pager, SegmentedControl,
} from '@parsina/framework';
import {
  Archive, CalendarHeart, Hourglass, Inbox, Presentation, SearchX,
} from 'lucide-react';

import { useTranslation } from '../../i18n';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useUrlState } from '../../lib/urlState';
import { useShortcut } from '../../lib/shortcuts';
import { crmErrorMessage, crmGql } from '../../lib/crmApi';
import { DOCTORSITE_LABEL } from '../../lib/runtimeEnv';
import {
  DEMO_PAGE_SIZE, OWNER_FILTERS, TABS, isFiltered, leadsVariables, pageCount,
} from '../../lib/demoRequestsView';
import {
  DEMO_INTENT, DOCTOR_PAGE_INTENT, LEAD_INTENTS, intentKey,
} from '../../constants/leadPipeline';
import { DEMO_LEADS_QUERY, OVERDUE_FOLLOW_UPS_QUERY } from './queries';
import useNewDemoCount from './useNewDemoCount';
import DemoRequestsToolbar from './DemoRequestsToolbar';
import DemoRequestsTable from './DemoRequestsTable';
import DemoRequestDrawer from './DemoRequestDrawer';

/** A lead id is a uuid; anything else in `?lead=` is junk and falls back to «no drawer». */
const LEAD_ID = /^[A-Za-z0-9-]{8,64}$/;

/**
 * The URL contract. Defaults are absent from the address; the only entity id
 * that ever appears is the lead's uuid (DESIGN_CONCEPT §13.2) — never a name,
 * a phone number or an e-mail.
 */
const SCHEMA = {
  // Which queue: demo requests (the default, so every existing link is
  // unchanged) or patients' appointment requests from doctor pages (§D7).
  intent: { default: DEMO_INTENT, values: LEAD_INTENTS },
  tab: { default: 'unanswered', values: TABS },
  owner: { default: 'all', values: OWNER_FILTERS },
  from: { default: '', parse: (raw) => (/^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null) },
  to: { default: '', parse: (raw) => (/^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null) },
  q: { default: '' },
  page: { default: 1, parse: (raw) => Number(raw) || 1 },
  lead: { default: '', parse: (raw) => (LEAD_ID.test(raw) ? raw : null) },
};

const EMPTY_ICON = {
  unanswered: Inbox, inProgress: Hourglass, closed: Archive, all: Presentation,
};

/** The list, server-paged and latest-wins; a refetch after a write dims, never blanks. */
function useLeadList(variables) {
  const { t } = useTranslation();
  const key = JSON.stringify(variables);
  const [data, setData] = React.useState({ leads: [], total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [stamp, setStamp] = React.useState(0);
  const seq = React.useRef(0);
  const first = React.useRef(true);

  const load = React.useCallback(({ quiet = false } = {}) => {
    seq.current += 1;
    const mine = seq.current;
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    crmGql(DEMO_LEADS_QUERY, JSON.parse(key))
      .then((d) => {
        if (mine !== seq.current) return;
        setData({ leads: d?.crmLeads?.leads || [], total: Number(d?.crmLeads?.total || 0) });
        setStamp((s) => s + 1);
      })
      .catch((e) => { if (mine === seq.current) setError(crmErrorMessage(e, t)); })
      .finally(() => {
        if (mine === seq.current) { setLoading(false); setRefreshing(false); }
      });
  }, [key, t]);

  React.useEffect(() => {
    load({ quiet: !first.current });
    first.current = false;
  }, [load]);

  return {
    ...data, loading, refreshing, error, stamp, reload: () => load({ quiet: true }), retry: () => load(),
  };
}

/** Lead ids with an OPEN follow-up already past due — a decoration, so a failure is silent. */
function useOverdueLeads(stamp) {
  const [ids, setIds] = React.useState(() => new Set());
  React.useEffect(() => {
    if (!stamp) return undefined;
    let live = true;
    crmGql(OVERDUE_FOLLOW_UPS_QUERY, { dueBeforeMs: String(Date.now()) })
      .then((d) => {
        if (!live) return;
        setIds(new Set((d?.crmFollowUps?.followUps || []).map((f) => f.leadId).filter(Boolean)));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [stamp]);
  return ids;
}

/**
 * «درخواست‌های دمو» — Archetype B (Report/List) over the CRM's demo-request
 * leads (docs/PARSINADENT_DEMO_REQUEST_DESIGN.md §3.2 T1–T10).
 *
 * ① PageHeader with the unanswered count · tabs (پاسخ‌داده‌نشده / در جریان /
 * بسته‌شده / همه) · ④ toolbar (owner, creation window, search) · ⑤ server-paged
 * table · ⑥ pager · the drawer at `?lead=<uuid>`.
 *
 * Everything filter-shaped lives in the URL. The data comes through `crmGql`
 * (the admin BFF's `/crm` pass-through to the CRM's own schema), so a CRM that
 * is down is this screen's error state with a retry and nothing else.
 */
export default function DemoRequests({ me }) {
  const { t, formatNumber } = useTranslation();
  useDocumentTitle('nav.title.demoRequests');
  const [view, setView] = useUrlState(SCHEMA);
  const mySub = me?.sub || null;

  const variables = React.useMemo(
    () => leadsVariables(view, { mySub }),
    // `lead` deliberately absent: opening a drawer must not refetch the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view.intent, view.tab, view.owner, view.from, view.to, view.q, view.page, mySub],
  );
  const list = useLeadList(variables);
  const overdue = useOverdueLeads(list.stamp);
  const newCount = useNewDemoCount({ intent: view.intent });
  const pageQueue = view.intent === DOCTOR_PAGE_INTENT;

  const go = React.useCallback((patch) => setView(patch), [setView]);
  // Switching queue keeps the tab and filters (they mean the same thing in
  // both), goes back to page 1 and closes a drawer that belongs to the other.
  const changeIntent = React.useCallback((intent) => {
    if (intent !== view.intent) setView({ intent, page: 1, lead: '' });
  }, [setView, view.intent]);
  const changeTab = React.useCallback((tab) => {
    if (tab !== view.tab) setView({ tab, page: 1 });
  }, [setView, view.tab]);
  // Opening a record is navigation (Back closes it); filters use replace.
  const openLead = React.useCallback((id) => setView({ lead: id }, { push: true }), [setView]);
  const closeLead = React.useCallback(() => setView({ lead: '' }), [setView]);
  const clearFilters = React.useCallback(() => {
    setView({ owner: 'all', from: '', to: '', q: '', page: 1 });
  }, [setView]);

  TABS.forEach((name, i) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useShortcut(String(i + 1), () => changeTab(name), {
      label: t('shortcuts.action.view', { view: t(`demoRequests.tabs.${name}`) }),
      group: t('shortcuts.groups.demoRequests'),
    });
  });
  // Listed, not claimed: the kit Table opens a focused row on Enter itself.
  useShortcut('enter', null, {
    label: t('shortcuts.action.openRow'),
    group: t('shortcuts.groups.demoRequests'),
    passive: true,
  });

  const filtered = isFiltered(view);
  const tabLabel = (name) => (name === 'unanswered' && newCount != null
    ? t('demoRequests.tabs.unansweredCount', { count: newCount })
    : t(`demoRequests.tabs.${name}`));

  const resultLabel = list.loading
    ? t('demoRequests.loading')
    : t('demoRequests.count', { count: list.total });

  const EmptyIcon = pageQueue ? CalendarHeart : (EMPTY_ICON[view.tab] || Presentation);
  const empty = filtered ? (
    <EmptyState
      icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
      title={t('demoRequests.emptyFiltered.title')}
      description={t('demoRequests.emptyFiltered.desc')}
      action={<Button variant="outline" onClick={clearFilters}>{t('common.actions.clearFilters')}</Button>}
      data-testid="demo-requests-empty"
    />
  ) : (
    <EmptyState
      icon={<EmptyIcon size={44} strokeWidth={1.5} aria-hidden />}
      title={pageQueue ? t('demoRequests.pageLeads.empty.title') : t(`demoRequests.empty.${view.tab}.title`)}
      description={pageQueue ? t('demoRequests.pageLeads.empty.desc') : t(`demoRequests.empty.${view.tab}.desc`)}
      data-testid="demo-requests-empty"
    />
  );

  return (
    <div className="relative flex h-full flex-col bg-background" data-testid="demo-requests">
      <PageHeader title={t('demoRequests.title')} sub={pageQueue ? t('demoRequests.pageLeads.sub', { site: DOCTORSITE_LABEL }) : t('demoRequests.sub')}>
        {newCount != null ? (
          <Badge
            variant={newCount > 0 ? 'warning' : 'outline'}
            className="text-xs"
            data-testid="demo-requests-new-count"
          >
            {t('demoRequests.newCount', { count: newCount })}
          </Badge>
        ) : null}
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <SegmentedControl
            semantics="filter"
            size="sm"
            ariaLabel={t('demoRequests.intent.label')}
            options={LEAD_INTENTS.map((value) => ({ value, label: t(intentKey(value)) }))}
            value={view.intent}
            onChange={changeIntent}
            data-testid="demo-requests-intent"
          />
          <SegmentedControl
            semantics="tabs"
            size="sm"
            ariaLabel={t('demoRequests.tabs.label')}
            options={TABS.map((value) => ({ value, label: tabLabel(value) }))}
            value={view.tab}
            onChange={changeTab}
            data-testid="demo-requests-tabs"
          />
        </div>

        <div role="tabpanel" aria-label={t(`demoRequests.tabs.${view.tab}`)} data-testid={`demo-requests-panel-${view.tab}`}>
          <DemoRequestsToolbar
            view={view}
            onChange={go}
            onClear={clearFilters}
            filtered={filtered}
            resultLabel={resultLabel}
            canFilterMine={!!mySub}
          />

          <div
            className="rounded-lg border border-border bg-card transition-opacity"
            style={{ opacity: list.refreshing ? 0.45 : 1 }}
            aria-busy={list.refreshing || undefined}
          >
            {list.error && !list.loading ? (
              <ErrorState
                title={t('demoRequests.error')}
                description={list.error}
                onRetry={list.retry}
                retryLabel={t('common.actions.retry')}
                data-testid="demo-requests-error"
              />
            ) : (
              <DemoRequestsTable
                intent={view.intent}
                rows={list.leads}
                loading={list.loading}
                mySub={mySub}
                overdue={overdue}
                onOpen={(r) => openLead(r.id)}
                empty={empty}
              />
            )}
          </div>

          {!list.loading && !list.error ? (
            <div data-testid="demo-requests-pager">
              <Pager
                page={Math.max(1, Number(view.page) || 1)}
                pageCount={pageCount(list.total)}
                pageSize={DEMO_PAGE_SIZE}
                onChange={(page) => go({ page })}
                prevLabel={t('common.pager.prev')}
                nextLabel={t('common.pager.next')}
                pageLabel={(p, total) => t('common.pager.label', { page: p, total })}
                formatNumber={formatNumber}
              />
            </div>
          ) : null}
        </div>
      </div>

      {view.lead ? (
        <DemoRequestDrawer
          key={view.lead}
          leadId={view.lead}
          mySub={mySub}
          onClose={closeLead}
          onChanged={list.reload}
          onOpenLead={openLead}
        />
      ) : null}
    </div>
  );
}
